import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Search, Loader2 } from 'lucide-react';
import { useCustomerSearch, type Customer } from '../../hooks/useCustomers';
import { Input } from '../ui';

interface CustomerSearchProps {
  onSelect: (customer: Customer) => void;
  onCreateNew?: () => void;
}

export function CustomerSearch({ onSelect, onCreateNew }: CustomerSearchProps) {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Use React Query for search with debounce
  const { data: results = [], isLoading } = useCustomerSearch(query);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Show dropdown when we have results
  useEffect(() => {
    if (query.length >= 2) {
      setShowDropdown(true);
    } else {
      setShowDropdown(false);
    }
  }, [results, query]);

  const handleSelect = (customer: Customer) => {
    onSelect(customer);
    setQuery(customer.name);
    setShowDropdown(false);
  };

  return (
    <div ref={containerRef} className="relative">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
        <Input
          type="text"
          placeholder={t('booking.selectCustomer')}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => query.length >= 2 && setShowDropdown(true)}
          className="pl-10"
        />
        {isLoading && (
          <Loader2 className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-gray-400" />
        )}
      </div>

      {showDropdown && (
        <div className="absolute z-10 mt-1 w-full rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5">
          <ul className="max-h-60 overflow-auto py-1">
            {results.length > 0 ? (
              results.map((customer) => (
                <li key={customer.id}>
                  <button
                    type="button"
                    className="w-full px-4 py-2 text-left hover:bg-gray-100"
                    onClick={() => handleSelect(customer)}
                  >
                    <div className="font-medium text-gray-900">{customer.name}</div>
                    <div className="text-sm text-gray-500">
                      {customer.email} - {customer.phone}
                    </div>
                  </button>
                </li>
              ))
            ) : query.length >= 2 && !isLoading ? (
              <li className="px-4 py-2 text-sm text-gray-500">
                {t('common.noResults')}
              </li>
            ) : null}

            {onCreateNew && (
              <li className="border-t">
                <button
                  type="button"
                  className="w-full px-4 py-2 text-left text-blue-600 hover:bg-gray-100"
                  onClick={onCreateNew}
                >
                  + {t('customer.newCustomer')}
                </button>
              </li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
}
